import React, { useState, useEffect } from 'react';
import styled, { withTheme } from 'styled-components';
import { DeviceIcon, FootnoteMarker } from 'packages/smart-home-support/components';

import { CalculatorSelectionItemContentProps, CalculatorSelectionItemSelectedProps } from '../../models';
import BREAK_POINTS from '../../../../styling/media-queries';

const CalculatorSelectionItemContainer = styled.div<CalculatorSelectionItemSelectedProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-radius: 10px;
  width: ${(props) => (props.allowTwoColumns && 146.26) || 80}px;
  height: ${(props) => (props.allowTwoColumns && 182.66) || 98.56}px;
  justify-items: center;
  border: 2px solid ${(props) => (props.isSelected ? props.theme.colors.calculatorBoxColor : '#E6E6EB')};
  overflow: hidden;
  margin: 5px;
  @media ${BREAK_POINTS.mobileBig} {
    width: ${(props) => (props.allowTwoColumns && 140) || 140}px;
    height: ${(props) => (props.allowTwoColumns && 135) || 135}px;
  }
`;

const CalculatorSelectionItemContentLabel = styled.div<{ allowTwoColumns?: boolean }>`
  font-style: normal;
  font-size: ${(props) => (props.allowTwoColumns ? 16 : 11)}px;
  text-align: center;
  line-height: 100%;
  color: #1b1b1b;
  overflow-wrap: break-word;
  padding-bottom: 1px;
  padding-left: 5px;
  padding-right: 5px;
  box-sizing: border-box;
  max-width: 100%;
  @media ${BREAK_POINTS.mobileBig} {
    font-size: 14px;
  }
`;

const CalculatorSelectionItemContentImage = styled.div<{ allowTwoColumns?: boolean }>`
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  max-width: ${(props) => (props.allowTwoColumns ? 83 : 40)}px;
  max-height: ${(props) => (props.allowTwoColumns ? 67 : 30.571)}px;
  margin-top: 14%;
  @media ${BREAK_POINTS.mobileBig} {
    max-width: 70px;
    max-height: 57px;
    width: 70px;
    height: 60px;
  }
`;

const CalculatorSelectionButtonContainer = styled.div<CalculatorSelectionItemSelectedProps>`
  background: ${(props) => (props.isSelected ? props.theme.colors.calculatorBoxColor : '#fff')};
  border-top: 2px solid ${(props) => (props.isSelected ? props.theme.colors.calculatorBoxColor : '#E6E6EB')};
  color: ${(props) => (props.isSelected ? props.theme.colors.calculatorBoxFontColor : '#1b1b1b')};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: ${(props) => (props.allowTwoColumns ? '18.5%' : '19px')};
  text-align: center;
  font-style: normal;
  font-weight: bold;
  font-size: 15px;
  font-size: ${(props) => (props.allowTwoColumns ? 24 : 15)}px;
  @media ${BREAK_POINTS.mobileBig} {
    font-size: 19px;
    height: 28px;
  }
`;

const CalculatorSelectionButton = styled.a`
  cursor: pointer;
  flex-shrink: 0;
  flex-grow: 1;
`;

const Value = styled.span`
  flex-shrink: 1;
  flex-grow: 0;
`;

import { PartnerThemeProps } from 'packages/smart-home-support/themes';

const CalculatorSelectionCounter: React.FunctionComponent<CalculatorSelectionItemContentProps> = (props) => {
  const { deviceName, onChangeDeviceQuantities, deviceQuantities } = props;
  const maxDeviceCount: number = 99;
  const minDeviceCount: number = 0;

  const increment = (): void => {
    const currentQuantity: number = deviceQuantities[deviceName] || 0;
    onChangeDeviceQuantities({
      ...deviceQuantities,
      [deviceName]: currentQuantity < maxDeviceCount ? currentQuantity + 1 : maxDeviceCount,
    });
  };
  const decrement = (): void => {
    const currentQuantity: number = deviceQuantities[deviceName] || 0;
    onChangeDeviceQuantities({
      ...deviceQuantities,
      [deviceName]: currentQuantity > minDeviceCount ? currentQuantity - 1 : minDeviceCount,
    });
  };

  return (
    <>
      <CalculatorSelectionButton className="val-calc-btn" onClick={decrement}>
        -
      </CalculatorSelectionButton>
      <Value>{deviceQuantities[deviceName] || 0}</Value>
      <CalculatorSelectionButton className="val-calc-btn" onClick={increment}>
        +
      </CalculatorSelectionButton>
    </>
  );
};

const CalculatorSelectionItemContent: React.FunctionComponent<
  CalculatorSelectionItemContentProps & PartnerThemeProps
> = (props) => {
  const { device, deviceName, hasDisclaimer, allowTwoColumns, isSelected = false } = props;

  return (
    <>
      <CalculatorSelectionItemContentImage allowTwoColumns={allowTwoColumns}>
        <DeviceIcon isAccented={isSelected} deviceType={deviceName as any} />{' '}
        {/* Might also need the two columns stuff */}
      </CalculatorSelectionItemContentImage>
      <CalculatorSelectionItemContentLabel allowTwoColumns={allowTwoColumns}>
        {device.label}
        {hasDisclaimer ? <FootnoteMarker /> : ''}
      </CalculatorSelectionItemContentLabel>
      <CalculatorSelectionButtonContainer isSelected={isSelected} allowTwoColumns={allowTwoColumns}>
        <CalculatorSelectionCounter {...props} />
      </CalculatorSelectionButtonContainer>
    </>
  );
};

const CalculatorSelectionItem: React.FunctionComponent<CalculatorSelectionItemContentProps & PartnerThemeProps> = (
  props
) => {
  const [isSelected, setIsSelected] = useState(false);
  const { deviceQuantities, deviceName } = props;

  useEffect(() => {
    setIsSelected(!!deviceQuantities[deviceName]);
  }, [deviceQuantities]);

  return (
    <CalculatorSelectionItemContainer
      isSelected={isSelected}
      className={'val-calc-item'}
      id={`val-calc-item-${deviceName}`}
      allowTwoColumns={props.allowTwoColumns}
    >
      <CalculatorSelectionItemContent {...props} isSelected={isSelected} />
    </CalculatorSelectionItemContainer>
  );
};

export default withTheme(CalculatorSelectionItem);



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/calculator/selection/components/CalculatorSelectionItem.tsx